import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { mediaServices } from "../../data/mediaServices";
import "../../styles/home.css";

const AUTOPLAY_MS = 2000;
const FADE_MS = 300;
const CLOSE_MS = 380;

const services = mediaServices.map((service, idx) => ({
  slug: service.slug,
  number: String(idx + 1).padStart(2, "0"),
  title: service.title,
  subtitle: service.hero,
  description: service.intro.split("\n\n")[0],
  img: service.image,
}));

const n = services.length;

const getOffset = (idx, activeIndex) => {
  let d = idx - activeIndex;
  if (d > n / 2) d -= n;
  if (d < -n / 2) d += n;
  return d;
};

// While the section fills the viewport, scroll input is captured and turned
// into one discrete card step per gesture (no free page scrolling shows).
// This is the gap enforced between steps so each card's 0.5s transform/fade
// finishes before the next begins.
const STEP_COOLDOWN_MS = 620;
// Fraction of the viewport the section must cover for it to "own" the scroll.
const ENGAGE_COVERAGE = 0.6;

const OurWork = () => {
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const sectionRef = useRef(null);
  const activeIndexRef = useRef(0);
  const lastStepRef = useRef(0);

  const active = services[displayIndex];
  const activeCard = services[activeIndex];

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // How much of the viewport the section currently covers (0–1).
  const coverage = () => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return 0;
    const vh = window.innerHeight;
    return Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) / vh;
  };

  // Auto-rotate the cards continuously, only skipping a beat right after a
  // manual scroll-step so the two don't double-advance.
  useEffect(() => {
    if (expanded) return undefined;
    const id = setInterval(() => {
      if (Date.now() - lastStepRef.current < AUTOPLAY_MS) return;
      setActiveIndex((i) => (i + 1) % n);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [expanded]);

  // Scroll takeover: while the section fills the viewport, each wheel / swipe /
  // arrow-key gesture steps exactly one card and the page itself stays put.
  // At the first card (scrolling up) or last card (scrolling down) the gesture
  // is released so the page scrolls on to the next section as normal.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || expanded) return undefined;

    const stepBy = (dir) => {
      if (!dir) return true;

      // At the first/last card and pushing further out — release right away so
      // the page scrolls on to the next section (never snap it back).
      const idx = activeIndexRef.current;
      if ((dir > 0 && idx === n - 1) || (dir < 0 && idx === 0)) return false;

      // Not aligned to the section's sticky offset yet — snap it into place,
      // then let the next gesture do the actual card step.
      const r = section.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(section).top) || 0;
      if (Math.abs(r.top - stickyTop) > 2) {
        window.scrollBy({ top: r.top - stickyTop, behavior: "auto" });
        return true;
      }

      const now = Date.now();
      if (now - lastStepRef.current < STEP_COOLDOWN_MS) return true;
      lastStepRef.current = now;
      setActiveIndex((i) => Math.max(0, Math.min(n - 1, i + dir)));
      return true;
    };

    const onWheel = (e) => {
      if (coverage() < ENGAGE_COVERAGE) return;
      if (stepBy(Math.sign(e.deltaY))) e.preventDefault();
    };

    let touchY = 0;
    const onTouchStart = (e) => {
      touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (coverage() < ENGAGE_COVERAGE) return;
      const dy = touchY - e.touches[0].clientY;
      if (Math.abs(dy) < 36) {
        e.preventDefault();
        return;
      }
      if (stepBy(Math.sign(dy))) {
        e.preventDefault();
        touchY = e.touches[0].clientY;
      }
    };

    const onKey = (e) => {
      if (coverage() < ENGAGE_COVERAGE) return;
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        if (stepBy(1)) e.preventDefault();
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        if (stepBy(-1)) e.preventDefault();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  useEffect(() => {
    if (activeIndex === displayIndex) return undefined;
    setFading(true);
    const t = setTimeout(() => {
      setDisplayIndex(activeIndex);
      setFading(false);
    }, FADE_MS);
    return () => clearTimeout(t);
  }, [activeIndex, displayIndex]);

  const go = (dir) => {
    if (expanded) return;
    setActiveIndex((i) => (i + dir + n) % n);
  };

  const handleCardClick = useCallback(
    (idx) => {
      if (expanded) return;
      if (getOffset(idx, activeIndex) === 0) {
        setExpanded(true);
      } else {
        setActiveIndex(idx);
      }
    },
    [expanded, activeIndex]
  );

  // Jumps past the card stepper to whatever follows the section's scroll track.
  const skip = () => {
    const track = sectionRef.current?.parentElement;
    if (!track) return;
    window.scrollTo({ top: track.getBoundingClientRect().bottom + window.scrollY, behavior: "smooth" });
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setExpanded(false);
      setClosing(false);
    }, CLOSE_MS);
  };

  return (
    <>
    <div className="ourwork-scroll-track">
    <section id="ourwork" className="svcx" ref={sectionRef}>
      <div className="svcx-bg" style={{ backgroundImage: `url(${activeCard.img})` }} />

      <div className="svcx-toplabel">
        <h2>Our&nbsp;<span className="section-heading-accent">Services</span></h2>
      </div>

      <div className="svcx-stage">
        <div className={`svcx-heading ${fading ? "is-fading" : ""}`}>
          <span className="svcx-number">{active.number}</span>
          <h1 className="svcx-title">{active.title}</h1>
          <p className="svcx-subtitle">{active.subtitle}</p>
          <p className="svcx-desc">{active.description}</p>
          <p className="svcx-hint">click card to explore</p>
        </div>
      </div>

      <div className="svcx-carousel">
        <button
          type="button"
          className="svcx-arrow svcx-arrow--left"
          onClick={() => go(-1)}
          aria-label="Previous"
        >
          <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="22" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
            <path d="M25 16L18 23L25 30" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="svcx-cards">
          {services.map((svc, idx) => {
            const offset = getOffset(idx, activeIndex);
            const abs = Math.abs(offset);
            if (abs > 2) return null;

            const isActive = offset === 0;
            const isHovered = hoveredIdx === idx && isActive && !expanded;

            const scale = isActive ? 1 : 1 - abs * 0.11;
            const tx = offset * 230;
            const ty = isActive ? (isHovered ? -22 : 0) : abs * 20;
            const rot = offset * 5;
            const zIndex = 10 - abs;

            return (
              <div
                key={svc.slug}
                className="svcx-card"
                onClick={() => handleCardClick(idx)}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{
                  transform: `translateX(calc(-50% + ${tx}px)) translateY(${ty}px) scale(${scale}) rotate(${rot}deg)`,
                  opacity: isActive ? 1 : 1 - abs * 0.28,
                  zIndex,
                }}
              >
                <div className={`svcx-card-inner ${isActive ? "is-active" : ""}`}>
                  <img src={svc.img} alt={svc.title} />
                  <div className="svcx-card-overlay" />
                  <div className="svcx-card-caption">
                    <p className="svcx-card-number">{svc.number}</p>
                    <p className={`svcx-card-title ${isActive ? "is-active" : ""}`}>{svc.title}</p>
                  </div>
                  {isActive && <span className="svcx-card-dot" />}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="svcx-arrow svcx-arrow--right"
          onClick={() => go(1)}
          aria-label="Next"
        >
          <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="22" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
            <path d="M21 16L28 23L21 30" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="svcx-dots">
          {services.map((svc, i) => (
            <div
              key={svc.slug}
              onClick={() => setActiveIndex(i)}
              className={`svcx-dot ${i === activeIndex ? "is-active" : ""}`}
            />
          ))}
        </div>
      </div>

      <button type="button" className="svcx-skip" onClick={skip} aria-label="Skip to next section">
        <span>Skip</span> <ArrowDown size={15} />
      </button>
    </section>
    </div>
    {(expanded || closing) &&
      createPortal(
        <div
          className={`svcx-overlay ${closing ? "is-closing" : "is-opening"}`}
          onClick={handleClose}
        >
          <div
            className={`svcx-overlay-card ${closing ? "is-closing" : "is-opening"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="svcx-overlay-text">
              <span className="svcx-overlay-kicker">{activeCard.number} — Our Services</span>
              <h2 className="svcx-overlay-title">{activeCard.title}</h2>
              <p className="svcx-overlay-desc">{activeCard.description}</p>
              <div className="svcx-overlay-actions">
                <button
                  type="button"
                  className="svcx-overlay-btn"
                  onClick={() => navigate(`/media-services/${activeCard.slug}`)}
                >
                  Know More &rarr;
                </button>
                <button type="button" className="svcx-overlay-close" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>

            <div className="svcx-overlay-media">
              <img src={activeCard.img} alt={activeCard.title} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OurWork;
